import NewTable from "components/NewTable";
import useRelatorioEmailColumns from "./hooks/useRelatorioEmailColumns";
import PageHeader from "components/Page/PageHeader";
import { useBreadcrumb } from "features/breadcrumbs";
import { useRelatoriosEmails } from "features/relatorios/hooks/useRelatoriosEmails";
import { FaFilter } from "react-icons/fa";
import { AiOutlinePlus } from "react-icons/ai";
import { Flex } from "@chakra-ui/react";
import DashboardEmail from "./components/DashboardEmail";
import ModalFiltro from "./components/ModalFiltro";
import useFiltroEmail from "./hooks/useFiltroEmail";
import { useState, useCallback } from "react";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import ModalHistoricoEmail from "./components/ModalHistoricoEmail";
import { EmailItemResponse } from "core/features/relatorios/typings";
import useReenviarEmails from "core/features/relatorios/hooks/useReenviarEmails";
import { useLoading } from "components/GlobalLoading";
import Container from "components/Container";
import { useRelatorioEmailPdf } from "features/relatorios/hooks/useRelatorioEmailPdf";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { exportExcelRelatorioEmail } from "./util/helper";
import RelatorioEmailPdfTemplate from "./PDFRelatorio";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import ModalAssinaturasRelatorio from "components/Relatorio/ModalAssinaturas";

function RelatorioEmailScreen() {
  const [selectedRows, setSelectedRows] = useState<EmailItemResponse[]>([]);
  const { filtro, onChangeFiltro, onResetFiltro } = useFiltroEmail();
  const { data, isLoading, refetch, isFetching } = useRelatoriosEmails(filtro);
  const [modalAssinaturasOpen, setModalAssinaturasOpen] = useState(false);

  const { isFetching: isFetchingPdf, refetch: refetchPdf } =
    useRelatorioEmailPdf(filtro, false);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Emails.pdf`,
    Component: RelatorioEmailPdfTemplate,
    loading: isFetching,
  });

  const _onExportPdf = useCallback(async () => {
    setModalAssinaturasOpen(true);
  }, []);

  const onSubmitAssinaturas = useCallback(
    async (assinaturas: AssinaturasItem[]) => {
      const { data } = await refetchPdf();

      if (data) {
        onExportPdf({
          relatorioData: { ...data, assinaturas },
        });
        setModalAssinaturasOpen(false);
      }
    },
    [onExportPdf, refetchPdf]
  );

  const _onExportExcel = useCallback(async () => {
    const { data } = await refetchPdf();

    exportExcelRelatorioEmail({
      emails: data?.emails || [],
      filtro,
    });
  }, [filtro, refetchPdf]);

  const columns = useRelatorioEmailColumns({
    filtro,
    onExportExcel: _onExportExcel,
    onExportPdf: _onExportPdf,
  });

  const { mutate: reenviarEmails, isLoading: isLoadingReenviar } =
    useReenviarEmails(refetch);

  const [openModal, setOpenModal] = useState(false);
  const [idUnidadeHistorico, setIdUnidadeHistorico] = useState<
    string | undefined
  >();

  useBreadcrumb([{ name: "Relatórios" }, { name: "Emails" }]);

  const numeroFiltroSelecionados =
    (filtro.assuntos?.length || 0) +
    (filtro.status ? 1 : 0) +
    (filtro.tipoDestinoId === TipoDestinoEnum.TodasUnidades || filtro.destinoId
      ? 1
      : 0) +
    (filtro.email ? 1 : 0) +
    (filtro.dataInicio || filtro.dataFim ? 1 : 0);

  useLoading(isLoadingReenviar || isFetchingPdf);
  return (
    <Container pb="32px">
      <PageHeader
        title="E-Mails Enviados"
        // titleButton="Reenviar"
        // iconButton={<MdOutgoingMail size={20} />}
        // onClick={() => ""}
        extraButtonTitle="Filtrar"
        extraButtonOnClick={() => setOpenModal(true)}
        badgeValueExtraButton={numeroFiltroSelecionados}
        badge
        iconExtraButton={
          <>
            <FaFilter size={14} />
            <Flex right="10px" top="14px" position="absolute">
              <AiOutlinePlus size={9} />
            </Flex>
          </>
        }
      />

      <DashboardEmail filtros={filtro} />

      <NewTable
        loading={isLoading || isFetching}
        columns={columns}
        rowKey={(item) => {
          return `${item.id}-${item.email}`;
        }}
        dataSource={data?.items || []}
        customPagination={{
          currentPage: data?.meta.currentPage ?? 0,
          totalItems: data?.meta.totalItems ?? 0,
          itemsPerPage: data?.meta.itemsPerPage ?? 0,
          onChangePage: (p) => onChangeFiltro({ page: p }),
        }}
        selectedRowsKeyExtractor={(_selectedRows) =>
          _selectedRows.map(
            (selectedRow) => `${selectedRow.id}-${selectedRow.email}`
          )
        }
        showCustomSelectionActions
        customSelectedRows={{
          handler: setSelectedRows,
          selecteds: selectedRows,
          actions: [
            {
              title: "Reenviar e-mails",
              onClick: () => reenviarEmails(selectedRows.map((s) => s.id)),
            },
          ],
        }}
        actions={[
          {
            title: "Reenviar e-mail",
            onClick: (v: EmailItemResponse) => reenviarEmails([v.id]),
          },
        ]}
        onRow={(v: EmailItemResponse) => {
          return {
            onClick: () => {
              setIdUnidadeHistorico(v.id);
            },
            style: {
              cursor: "pointer",
            },
          };
        }}
      />
      <ModalFiltro
        onCancel={() => setOpenModal(false)}
        onReset={onResetFiltro}
        filtro={filtro}
        open={openModal}
        onChangeFiltro={onChangeFiltro}
        onClose={() => setOpenModal(false)}
      />
      <ModalHistoricoEmail
        onClose={() => setIdUnidadeHistorico(undefined)}
        id={idUnidadeHistorico}
        isOpenModal={!!idUnidadeHistorico}
      />

      <ModalAssinaturasRelatorio
        isOpenModal={modalAssinaturasOpen}
        onClose={() => setModalAssinaturasOpen(false)}
        onSubmit={onSubmitAssinaturas}
      />
    </Container>
  );
}

export default RelatorioEmailScreen;
